import { useEffect, useState, useCallback } from "react";
import {
  Percent, BadgePoundSterling, Lock, RotateCcw, Save, Loader2, Eye, EyeOff,
  ToggleRight, ToggleLeft, Edit3,
} from "lucide-react";
import { adminApi } from "@/lib/adminApi";
import { toast } from "sonner";
import { fmtMoney } from "@/pages/admin/util";

/* Workspace markup card.
   Lives inside the Finance tab. Lets the operator:
     - toggle the 20% auto-markup on/off
     - change the markup %
     - reset to 20%
     - switch to a manual price override and type the client price
     - live-preview the breakdown before saving
   Saves via PATCH /admin/cases/{id}/markup; the response is the authoritative
   pricing block from the server which we lift into the workspace state.
*/

const DEFAULT_PCT = 20;

export default function MarkupCard({ caseId, pricing, onSaved }) {
  // Working copy — the operator edits this. We only save when the user clicks Save.
  const [draft, setDraft] = useState(pricing);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Sync from props whenever the workspace reloads.
  useEffect(() => { setDraft(pricing); setDirty(false); }, [pricing]);

  const recalc = useCallback((next) => {
    // Live preview computation — must match the backend formulas exactly.
    const cost = Number(next.contractor_cost_ex_vat) || 0;
    const enabled = !!next.markup_enabled;
    const pct = enabled ? (Number(next.markup_percentage) || 0) : 0;
    const override = !!next.manual_price_override;
    const manualPrice = override ? (Number(next.client_price_ex_vat) || 0) : null;
    const preDiscount = override
      ? manualPrice
      : Math.round((cost * (1 + pct / 100)) * 100) / 100;
    // Member discount applies AFTER markup, never to contractor cost.
    const mdEnabled = !!next.member_discount_enabled;
    const mdPct = mdEnabled ? (Number(next.member_discount_percentage) || 0) : 0;
    const mdAmount = Math.round((preDiscount * mdPct / 100) * 100) / 100;
    const client = Math.round((preDiscount - mdAmount) * 100) / 100;
    const markupAmount = Math.round((preDiscount - cost) * 100) / 100;
    const profit = Math.round((client - cost) * 100) / 100;
    const marginPct = client > 0 ? Math.round(((profit / client) * 100) * 100) / 100 : 0;
    const vatRate = Number(next.vat_rate) || 20;
    const vatAmount = Math.round((client * vatRate / 100) * 100) / 100;
    const inc = Math.round((client + vatAmount) * 100) / 100;
    return {
      ...next,
      markup_percentage: pct,
      client_price_ex_vat: client,
      client_price_before_discount: preDiscount,
      member_discount_percentage: mdPct,
      member_discount_amount: mdAmount,
      markup_amount: markupAmount,
      gross_profit: profit,
      margin_percentage: marginPct,
      vat_rate: vatRate,
      vat_amount: vatAmount,
      client_price_inc_vat: inc,
    };
  }, []);

  const patchDraft = (changes) => {
    setDraft((d) => recalc({ ...d, ...changes }));
    setDirty(true);
  };

  const toggleAuto = () => patchDraft({
    markup_enabled: !draft.markup_enabled,
    manual_price_override: false,
  });

  const toggleOverride = () => patchDraft({
    manual_price_override: !draft.manual_price_override,
    // When switching ON override, seed the editable price from the current computed value.
    client_price_ex_vat: !draft.manual_price_override ? draft.client_price_ex_vat : draft.client_price_ex_vat,
  });

  const resetToDefault = () => patchDraft({
    markup_enabled: true,
    markup_percentage: DEFAULT_PCT,
    manual_price_override: false,
  });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        contractor_cost_ex_vat: Number(draft.contractor_cost_ex_vat) || 0,
        markup_enabled: !!draft.markup_enabled,
        markup_percentage: Number(draft.markup_percentage) || 0,
        manual_price_override: !!draft.manual_price_override,
        vat_rate: Number(draft.vat_rate) || 20,
        member_discount_enabled: !!draft.member_discount_enabled,
        member_discount_percentage: Number(draft.member_discount_percentage) || 0,
      };
      if (draft.manual_price_override) {
        // For manual override save the pre-discount price so the backend can
        // reapply the discount cleanly when computing the final figure.
        payload.client_price_ex_vat = Number(draft.client_price_before_discount ?? draft.client_price_ex_vat) || 0;
      }
      const { data } = await adminApi.patch(`/cases/${caseId}/markup`, payload);
      toast.success("Markup saved");
      setDirty(false);
      onSaved?.(data.pricing);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save markup");
    } finally {
      setSaving(false);
    }
  };

  const auto = !!draft.markup_enabled && !draft.manual_price_override;
  const override = !!draft.manual_price_override;

  return (
    <section className="bg-white border border-[#E2DCD0] rounded-xl overflow-hidden shadow-[0_1px_0_rgba(7,26,51,0.04)] min-w-0 max-w-full" data-testid="ws-markup-card">
      <header className="px-5 py-4 border-b border-[#E2DCD0] bg-gradient-to-b from-[#F7F4ED]/80 to-white flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[10.5px] tracking-[0.22em] uppercase text-[#B88A2A] font-semibold">Pricing & Markup</p>
          <h3 className="font-display text-lg text-[#071A33] mt-0.5 tracking-tight">Contractor cost → client price</h3>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => patchDraft({ markup_enabled: true, markup_percentage: DEFAULT_PCT, manual_price_override: false })}
            className="text-[12px] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#064E2E] text-white hover:bg-[#0a5b55]"
            data-testid="markup-apply-20"
          >
            Apply 20%
          </button>
          <button
            onClick={() => {
              const v = window.prompt("Custom markup %", String(draft.markup_percentage || DEFAULT_PCT));
              if (v === null) return;
              const n = Number(v);
              if (Number.isFinite(n) && n >= 0 && n <= 1000) patchDraft({ markup_enabled: true, markup_percentage: n, manual_price_override: false });
              else toast.error("Enter a percentage between 0 and 1000");
            }}
            className="text-[12px] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#E2DCD0] text-[#475569] hover:text-[#071A33] hover:border-[#0F766E]"
            data-testid="markup-apply-custom"
          >
            Apply Custom %
          </button>
          <button
            onClick={resetToDefault}
            className="text-[12px] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#E2DCD0] text-[#475569] hover:text-[#071A33] hover:border-[#0F766E]"
            data-testid="markup-reset-btn"
          >
            <RotateCcw size={12}/> Reset
          </button>
        </div>
      </header>

      <div className="grid grid-cols-1 2xl:grid-cols-[1fr,320px] gap-0 2xl:divide-x divide-[#E2DCD0]">
        {/* CONTROLS */}
        <div className="p-5 space-y-4 min-w-0">
          {/* Contractor cost — always editable. */}
          <Field label="Contractor cost (ex VAT)" testid="markup-cost-field">
            <div className="flex items-center gap-2">
              <span className="text-[#475569]">£</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={draft.contractor_cost_ex_vat ?? ""}
                onChange={(e) => patchDraft({ contractor_cost_ex_vat: e.target.value })}
                className="field-input m-0 h-10 max-w-[200px]"
                data-testid="markup-cost-input"
              />
            </div>
          </Field>

          {/* Auto-markup toggle row */}
          <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-[#F7F4ED]/60 border border-[#E2DCD0]" data-testid="markup-auto-toggle-row">
            <div className="flex items-start gap-3">
              <button
                onClick={toggleAuto}
                disabled={override}
                className={`shrink-0 mt-0.5 ${override ? "opacity-40 cursor-not-allowed" : ""}`}
                data-testid="markup-auto-toggle"
                aria-pressed={auto}
              >
                {auto ? <ToggleRight size={28} className="text-[#064E2E]"/> : <ToggleLeft size={28} className="text-[#94A3B8]"/>}
              </button>
              <div>
                <p className="text-sm font-medium text-[#071A33]">Auto-add {DEFAULT_PCT}% markup</p>
                <p className="text-[11px] text-[#475569] mt-0.5">Client price = contractor cost × (1 + markup ÷ 100). Default is on at {DEFAULT_PCT}%.</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                step="0.5"
                min="0"
                max="1000"
                disabled={!auto}
                value={draft.markup_percentage ?? 0}
                onChange={(e) => patchDraft({ markup_percentage: e.target.value })}
                className={`field-input m-0 h-9 w-24 text-right tabular-nums ${!auto ? "opacity-50" : ""}`}
                data-testid="markup-percent-input"
              />
              <span className="text-sm text-[#475569]">%</span>
            </div>
          </div>

          {/* Manual override row */}
          <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-[#F7F4ED]/60 border border-[#E2DCD0]" data-testid="markup-override-row">
            <div className="flex items-start gap-3">
              <button onClick={toggleOverride} className="shrink-0 mt-0.5" data-testid="markup-override-toggle" aria-pressed={override}>
                {override ? <ToggleRight size={28} className="text-[#B88A2A]"/> : <ToggleLeft size={28} className="text-[#94A3B8]"/>}
              </button>
              <div>
                <p className="text-sm font-medium text-[#071A33] flex items-center gap-1.5"><Edit3 size={13} className="text-[#B88A2A]"/>Manual price override</p>
                <p className="text-[11px] text-[#475569] mt-0.5">Type the client price directly. Ignores markup %.</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[#475569] text-sm">£</span>
              <input
                type="number"
                step="0.01"
                min="0"
                disabled={!override}
                value={draft.client_price_ex_vat ?? ""}
                onChange={(e) => patchDraft({ client_price_ex_vat: e.target.value })}
                className={`field-input m-0 h-9 w-32 tabular-nums ${!override ? "opacity-50" : ""}`}
                data-testid="markup-override-input"
              />
            </div>
          </div>

          {/* Member discount row */}
          <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-[#0F766E]/5 border border-[#0F766E]/30" data-testid="markup-member-row">
            <div className="flex items-start gap-3">
              <button
                onClick={() => patchDraft({
                  member_discount_enabled: !draft.member_discount_enabled,
                  member_discount_percentage: draft.member_discount_percentage || 10,
                })}
                className="shrink-0 mt-0.5"
                data-testid="markup-member-toggle"
                aria-pressed={!!draft.member_discount_enabled}
              >
                {draft.member_discount_enabled
                  ? <ToggleRight size={28} className="text-[#0F766E]"/>
                  : <ToggleLeft size={28} className="text-[#94A3B8]"/>}
              </button>
              <div>
                <p className="text-sm font-medium text-[#071A33]">Member discount</p>
                <p className="text-[11px] text-[#475569] mt-0.5">Apply a customer discount on the client price. Member-only price.</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                step="0.5"
                min="0"
                max="100"
                disabled={!draft.member_discount_enabled}
                value={draft.member_discount_percentage ?? 0}
                onChange={(e) => patchDraft({ member_discount_percentage: e.target.value })}
                className={`field-input m-0 h-9 w-24 text-right tabular-nums ${!draft.member_discount_enabled ? "opacity-50" : ""}`}
                data-testid="markup-member-input"
              />
              <span className="text-sm text-[#475569]">%</span>
            </div>
          </div>

          {/* Save / dirty bar */}
          <div className="flex items-center justify-between pt-2 border-t border-[#E2DCD0]">
            <p className="text-[11px] text-[#475569]" data-testid="markup-dirty-state">
              {dirty ? "Unsaved changes — review the breakdown on the right." : "All saved."}
            </p>
            <button
              onClick={save}
              disabled={!dirty || saving}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-semibold transition ${
                !dirty || saving ? "bg-[#E2DCD0] text-[#94A3B8] cursor-not-allowed" : "bg-[#064E2E] text-white hover:bg-[#0a5b55]"
              }`}
              data-testid="markup-save-btn"
            >
              {saving ? <Loader2 size={13} className="animate-spin"/> : <Save size={13}/>}
              Save changes
            </button>
          </div>
        </div>

        {/* LIVE BREAKDOWN */}
        <div className="p-5 bg-[#071A33] text-white 2xl:rounded-r-xl border-t 2xl:border-t-0 border-[#E2DCD0] space-y-3 min-w-0" data-testid="markup-breakdown">
          <p className="text-[10.5px] tracking-[0.22em] uppercase text-[#B88A2A] font-semibold">Internal breakdown</p>
          <BreakdownRow label="Contractor cost" value={fmtMoney(draft.contractor_cost_ex_vat)} sub="ex VAT"/>
          <BreakdownRow
            label={override ? "Override applied" : `Markup ${draft.markup_percentage || 0}%`}
            value={(override ? "" : "+") + fmtMoney(draft.markup_amount)}
            sub={override ? "manual" : "auto"}
            accent={draft.markup_amount > 0 ? "gold" : "muted"}
          />
          {draft.member_discount_enabled && (draft.member_discount_amount > 0) && (
            <BreakdownRow
              label={`Member discount ${draft.member_discount_percentage || 0}%`}
              value={`−${fmtMoney(draft.member_discount_amount)}`}
              sub="customer"
              accent="muted"
            />
          )}
          <div className="h-px bg-white/10 my-2"/>
          <BreakdownRow label="Client price" value={fmtMoney(draft.client_price_ex_vat)} sub="ex VAT" accent="bright"/>
          <BreakdownRow label="VAT" value={fmtMoney(draft.vat_amount)} sub={`${draft.vat_rate || 20}% rate`}/>
          <BreakdownRow label="Client total" value={fmtMoney(draft.client_price_inc_vat)} sub="inc VAT" accent="bright"/>
          <div className="h-px bg-white/10 my-2"/>
          <BreakdownRow label="Gross profit" value={fmtMoney(draft.gross_profit)} accent={draft.gross_profit > 0 ? "gold" : "muted"}/>
          <BreakdownRow label="Margin" value={`${(draft.margin_percentage || 0).toFixed(2)}%`} accent={draft.margin_percentage > 0 ? "gold" : "muted"}/>

          <p className="text-[10px] text-white/40 leading-relaxed flex items-start gap-1.5 pt-2 border-t border-white/10">
            <Lock size={10} className="mt-0.5 shrink-0"/>
            Resident-facing views show <strong className="text-white/70">only the client price</strong>. Contractor cost and margin never leave admin.
          </p>
        </div>
      </div>
    </section>
  );
}

function Field({ label, testid, children }) {
  return (
    <div data-testid={testid}>
      <label className="text-[10.5px] tracking-[0.22em] uppercase text-[#475569] font-medium block mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function BreakdownRow({ label, value, sub, accent }) {
  const valueClass =
    accent === "bright" ? "text-white" :
    accent === "gold"   ? "text-[#F6C56C]" :
                          "text-white/85";
  return (
    <div className="flex items-baseline justify-between gap-3 min-w-0">
      <p className="text-[12.5px] text-white/65 min-w-0 truncate">{label}{sub && <span className="text-white/35 ml-2">{sub}</span>}</p>
      <p className={`tabular-nums font-medium whitespace-nowrap ${valueClass}`}>{value}</p>
    </div>
  );
}
